"use client";

/**
 * Portfolio grid with working tag filter + redesigned cards.
 *
 * Cards have:
 *   - A gradient hero (per-tag palette so each category reads visually).
 *   - A category chip top-left and an animated arrow top-right that pops
 *     on hover.
 *   - A soft accent glow that fades in on hover via CSS opacity.
 *   - A subtle scale-in entry animation when filtered into view.
 */

import type { PortfolioItem } from "@/types";

type Props = { items: PortfolioItem[] };

const GRADIENT_BY_TAG: Record<string, string> = {
  AI: "from-violet-700 via-indigo-800 to-slate-950",
  "AI Agents": "from-sky-700 via-blue-800 to-slate-950",
  Web: "from-emerald-700 via-teal-800 to-slate-950",
  Mobile: "from-rose-700 via-fuchsia-800 to-slate-950",
  Healthcare: "from-cyan-700 via-sky-800 to-slate-950",
  "E-commerce": "from-amber-700 via-orange-800 to-slate-950",
  Blockchain: "from-indigo-700 via-violet-800 to-slate-950",
};

export default function PortfolioList({ items }: Props) {
  return (
    <>
      {items.length === 0 ? (
        <div className="mt-16 rounded-2xl border border-[var(--stroke)] bg-[var(--panel)]/60 p-12 text-center">
          <p className="text-lg font-semibold">No projects yet.</p>
          <p className="mt-2 text-sm text-[var(--muted)]">Check back soon.</p>
        </div>
      ) : (
        <section className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => {
            const gradient = GRADIENT_BY_TAG[item.tag] ?? "from-slate-700 via-slate-800 to-slate-950";
            return (
              <article
                key={item.slug ?? item.title}
                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--stroke)] bg-[var(--panel)]/60 transition duration-300 hover:-translate-y-1 hover:border-[var(--accent)]/50 hover:shadow-[0_20px_60px_-30px_var(--accent)]"
                style={{ animation: "portfolioIn 380ms ease-out both" }}
              >
                {/* Hover-only accent glow */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-0 blur-3xl transition duration-500 group-hover:opacity-60"
                  style={{ background: "radial-gradient(circle, var(--accent-soft), transparent 70%)" }}
                />

                <div
                  className={`relative h-75 overflow-hidden ${item.image ? "bg-slate-900" : `bg-gradient-to-br ${gradient}`}`}
                  style={item.image ? { backgroundImage: `url("${item.image}")`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                >
                </div>

                <div className="flex flex-1 flex-col gap-2 p-5">
                  {item.company ? (
                    <p className="text-xs uppercase tracking-[0.16em] text-[var(--subtle)]">
                      {item.company}
                    </p>
                  ) : item.subtitle ? (
                    <p className="text-xs text-[var(--subtle)]">{item.subtitle}</p>
                  ) : null}
                  <h2 className="text-lg font-bold leading-snug text-[var(--text)] transition group-hover:text-[var(--accent)]">
                    {item.title}
                  </h2>
                  {item.desc ? (
                    <p className="line-clamp-3 mb-3 text-sm text-[var(--muted)]">{item.desc}</p>
                  ) : null}
                  <div className="mt-auto flex flex-wrap items-center gap-2">
                    {item.technology ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[var(--stroke)] bg-white/5 px-3 py-1 text-[0.7rem] font-semibold text-[var(--text)]">
                        {item.technology}
                      </span>
                    ) : null}
                    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--accent)]/30 bg-[var(--accent)]/10 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-[var(--accent)]">
                      {item.tag}
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      )}

      <style>{`
        @keyframes portfolioIn {
          from { opacity: 0; transform: translateY(8px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}
